import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import DragHandleIcon from '@material-ui/icons/DragHandle';

import { SdmTypography } from 'components';
import CustomAccordion from '../styledComponents/CustomAccordion';
import BlockAccordionTitle from '../styledComponents/BlockAccordionTitle';
import translations from 'utils/translations';
import {
  FormReactFCEnum,
  handleMultipleDropAfterErrors,
  ParagraphEnum,
  useEntities,
} from 'utils/drupalEntities';

import SharedControllers from './SharedControllers';
import { LoaderContext } from 'context/LoaderContextProvider';
import MultipleDropZonePreviewImage from '../styledComponents/MultipleDropZonePreviewImage';
import CustomTextFieldComponent from '../styledComponents/CustomTextFieldComponent';
import { Controller } from 'react-hook-form';
import LabelContainer from './LabelContainer';
import SimpleRichText from '../styledComponents/SimpleRichText';
import NotTranslatableMsg from './NotTranslatableMsg';

const DetailSlider = ({
  control,
  field,
  idx,
  additionalData,
  handleDelete,
  watch,
  setValue,
  setCustomErrors,
  langCode,
  getValues,
  productLangCode,
  setInfoDialog,
  ...listeners
}) => {
  const intl = useIntl();
  const i18n = translations.productPage(intl);
  const { setLoaderActive } = useContext(LoaderContext);
  const { listEntityReferenceRevisionsFieldsForm } = useEntities();
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [form, setForm] = useState();
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [triggerValue, setTriggerValue] = useState(-1);

  const [caption, setCaption] = useState([]);
  const [alt, setAlt] = useState([]);
  const [position, setPosition] = useState([]);
  const [oldRevisionId, setOldRevisionId] = useState([]);

  const isEn = productLangCode === 'en';
  const isNotTranslatable = (key) => isEn && !form?.[key]?.isTranslatable;

  const isNotImgTranslatable = (key) => isEn && !form?.[key]?.isImgTranslatable;
  const isNotAltTranslatable = (key) => isEn && !form?.[key]?.isAltTranslatable;

  //Loader Function
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);

  useEffect(() => {
    if (additionalData) {
      const fetchChildren = async (form) => {
        try {
          showLoader();
          // Call Lambda endpoint to fetch Forms
          const components = await listEntityReferenceRevisionsFieldsForm({
            formBundle: Object.values(form.formBundle)[0],
            locale: langCode,
          });
          const formArray = Object.values(components).flat();
          const img = formArray.find(
            (c) => c.formComponent === FormReactFCEnum.IMAGE_DROPZONE
          );
          const txt = formArray.find(
            (c) => c.formComponent === FormReactFCEnum.RICH_TEXT
          );
          setForm({ image: img, richText: txt });
        } catch (error) {
          setForm();
        } finally {
          hideLoader();
        }
      };
      Object.entries(additionalData).map(([k, v]) => {
        if (k === ParagraphEnum.SLIDER_DETAIL) {
          fetchChildren(v[0]);
        }
      });
    }
  }, [additionalData]);

  useEffect(() => {
    if (
      field?.fileCaptions?.length ||
      field?.fileAlts?.length ||
      field?.filePositions?.length ||
      field?.oldRevisionId?.length
    ) {
      setAlt(field?.fileAlts);
      setCaption(field?.fileCaptions);
      setPosition(field?.filePositions);
      setOldRevisionId(field?.oldRevisionId);
    }
  }, [field]);

  useEffect(() => {
    if (alt?.length) setValue(`additionalContents[${idx}].fileAlts`, alt);
    if (caption?.length)
      setValue(`additionalContents[${idx}].fileCaptions`, caption);
    if (position?.length)
      setValue(`additionalContents[${idx}].filePositions`, position);
    if (oldRevisionId?.length)
      setValue(`additionalContents[${idx}].oldRevisionId`, oldRevisionId);
  }, [alt, caption, position, oldRevisionId]);

  const handleChange = (acceptedFiles) => {
    const c = form.image;
    const wFiles = watch(`additionalContents[${idx}]`);
    handleMultipleDropAfterErrors({
      files: acceptedFiles,
      fieldName: `additionalContents[${idx}][${form.image.name}]`,
      maxResolution: c.maxResolution,
      minResolution: c.minResolution,
      maxSize: c.maxSize,
      fileExtension: c.extensions.split(' '),
      setCustomErrors,
      setValue,
      i18nErrors: i18n.errors,
      values: wFiles || [],
      setCaption,
      setAlt,
      setPosition,
      setOldRevisionId,
    });
  };

  const handleImgClick = (index) => {
    setTriggerValue((prev) => prev + 1);
    setSelectedIdx(index);
  };

  const handleRemoved = (index) => {
    const _filteredAlt = alt.filter((v, idx) => idx !== index);
    const _filteredCaption = caption.filter((v, idx) => idx !== index);
    const _filteredPosition = position.filter((v, idx) => idx !== index);
    setCaption(_filteredCaption);
    setAlt(_filteredAlt);
    setPosition(_filteredPosition);
  };

  return (
    <>
      {form && (
        <CustomAccordion
          onChange={(expanded) => {
            setIsAccordionOpen(expanded);
          }}
          expandIconVariant="secondary"
          title={
            <BlockAccordionTitle
              productLangCode={productLangCode}
              langCode={langCode}
              setInfoDialog={setInfoDialog}
              handleDelete={handleDelete}
              formKey={ParagraphEnum.SLIDER_DETAIL}
              formTitle={i18n.titles.detailSlider}
            >
              <div
                style={{ width: '100%', display: 'flex', alignItems: 'center' }}
              >
                {!isAccordionOpen && productLangCode?.toLowerCase() !== 'en' ? (
                  <div {...listeners} style={{ marginRight: '5px' }}>
                    <DragHandleIcon
                      style={{
                        height: '15px',
                        color: 'gray',
                      }}
                    />
                  </div>
                ) : null}
                <div>
                  <SdmTypography
                    variant={'h3'}
                    margin={0}
                    style={{ textTransform: 'uppercase' }}
                  >
                    {i18n.titles.detailSlider}
                  </SdmTypography>
                </div>
              </div>
            </BlockAccordionTitle>
          }
        >
          <LabelContainer
            label={`${form.image?.label}`}
            description={form.image?.description}
            isRequired={form.image?.isRequired}
            hideSpacer
          />
          {isNotTranslatable('image') ? <NotTranslatableMsg /> : null}
          <MultipleDropZonePreviewImage
            control={control}
            watch={watch}
            setValue={setValue}
            defaultValue={field[form.image.name]}
            name={`additionalContents[${idx}][${form.image.name}]`}
            accept={{
              'image/*': form.image.extensions
                ? form.image.extensions.split(' ').map((e) => `.${e}`)
                : ['jpg'],
            }}
            handleChange={handleChange}
            specs={[
              // i18n.imageSpecs.number,
              `${i18n.imageSpecs.size} ${form.image.maxSize}`,
              `${i18n.imageSpecs.format} ${form.image.extensions}`,
              `${i18n.imageSpecs.logoSpecSizeMin} ${form.image.minResolution}px ${i18n.imageSpecs.logoSpecSizeAnd} ${form.image.maxResolution}px`,
            ]}
            handleImgClick={handleImgClick}
            onRemoved={handleRemoved}
            parentComponentIdx={idx}
            tooltipText={i18n.tooltip.sliderHover}
            disabled={isNotImgTranslatable('image')}
            maxResolution={form.image.maxResolution}
            minResolution={form.image.minResolution}
            setCustomErrors={setCustomErrors}
          />
          {triggerValue >= 0 ? (
            <>
              <LabelContainer
                label={i18n.buttons.alt}
                description={i18n.buttons.altDescription}
              />
              <Controller
                name={`additionalContents[${idx}].fileAlts[${selectedIdx}]`}
                control={control}
                defaultValue={alt?.[selectedIdx] || ''}
                key={triggerValue}
                render={(props) => {
                  return (
                    <CustomTextFieldComponent
                      variant="outlined"
                      label={i18n.buttons.alt}
                      placeholder={i18n.buttons.altPlaceholder}
                      style={{ width: '100%', alignSelf: 'end' }}
                      onChange={(e) => {
                        const prev = [...alt];
                        prev[selectedIdx] = e.target.value;
                        setAlt(prev);
                        props.onChange(e.target.value);
                      }}
                      value={props.value}
                      disabled={isNotAltTranslatable('image')}
                    />
                  );
                }}
              />
            </>
          ) : null}
          {triggerValue >= 0 ? (
            <>
              <LabelContainer
                label={`${form.richText?.label}`}
                description={form.richText?.description}
                isRequired={form.richText?.isRequired}
              />
              <SimpleRichText
                control={control}
                name={`additionalContents[${idx}].fileCaptions[${selectedIdx}]`}
                getValues={getValues}
                defaultValue={caption?.[selectedIdx] || ''}
                key={triggerValue}
                customOnChange={(value) => {
                  const prev = [...caption];
                  prev[selectedIdx] = value ? JSON.stringify(value) : '';
                  setCaption(prev);
                }}
                disabled={isNotTranslatable('richText')}
              />
            </>
          ) : null}

          <SharedControllers control={control} field={field} idx={idx} />
          <Controller
            control={control}
            name={`additionalContents[${idx}].fileAlts`}
            as={<input hidden />}
            defaultValue={field.fileAlts}
          />
          <Controller
            control={control}
            name={`additionalContents[${idx}].fileCaptions`}
            as={<input hidden />}
            defaultValue={field.fileCaptions}
          />
          <Controller
            control={control}
            name={`additionalContents[${idx}].filePositions`}
            as={<input hidden />}
            defaultValue={field.filePositions}
          />
          <Controller
            control={control}
            name={`additionalContents[${idx}].oldRevisionId`}
            as={<input hidden />}
            defaultValue={field.oldRevisionId}
          />
        </CustomAccordion>
      )}
    </>
  );
};

export default DetailSlider;
